import { Injectable } from '@angular/core';
import { GoogleIdentity } from '@interfaces/User';
import { environment } from 'environments/environment';
import {
  FirebaseApp,
  FirebaseOptions,
  getApps,
  initializeApp,
} from 'firebase/app';
import {
  Auth,
  GoogleAuthProvider,
  User,
  browserSessionPersistence,
  getAuth,
  getRedirectResult,
  setPersistence,
  signInWithPopup,
  signOut,
} from 'firebase/auth';

@Injectable({
  providedIn: 'root',
})
export class GoogleIdentityService {
  private readonly appName = 'garage-digital-google-auth';
  private authPromise: Promise<Auth> | null = null;

  get isEnabled(): boolean {
    return environment.googleAuthEnabled && environment.firebase !== null;
  }

  public async startSignIn(): Promise<GoogleIdentity | null> {
    const auth = await this.getFirebaseAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    const credential = await signInWithPopup(auth, provider);
    return this.toIdentity(credential.user);
  }

  public async completeRedirectSignIn(): Promise<GoogleIdentity | null> {
    if (!this.isEnabled) return null;

    const auth = await this.getFirebaseAuth();
    const credential = await getRedirectResult(auth);

    return credential ? this.toIdentity(credential.user) : null;
  }

  public async getCurrentIdentity(): Promise<GoogleIdentity | null> {
    if (!this.isEnabled) return null;

    const auth = await this.getFirebaseAuth();
    await auth.authStateReady();

    return auth.currentUser ? this.toIdentity(auth.currentUser) : null;
  }

  public async signOut(): Promise<void> {
    if (!this.isEnabled) return;

    await signOut(await this.getFirebaseAuth());
  }

  private getFirebaseAuth(): Promise<Auth> {
    if (!this.isEnabled || !environment.firebase) {
      return Promise.reject(
        new Error('La autenticación con Google no está disponible.'),
      );
    }

    if (!this.authPromise) {
      this.authPromise = this.initializeAuth(environment.firebase);
    }

    return this.authPromise;
  }

  private async initializeAuth(config: FirebaseOptions): Promise<Auth> {
    const existingApp = getApps().find((app) => app.name === this.appName);
    const app = existingApp ?? initializeApp(config, this.appName);

    if (existingApp && !this.hasExpectedConfig(existingApp, config)) {
      throw new Error(
        'La aplicación de Firebase para Google tiene una configuración inesperada.',
      );
    }

    const auth = getAuth(app);
    await setPersistence(auth, browserSessionPersistence);

    return auth;
  }

  private hasExpectedConfig(
    app: FirebaseApp,
    config: FirebaseOptions,
  ): boolean {
    return (
      app.options.apiKey === config.apiKey &&
      app.options.authDomain === config.authDomain &&
      app.options.projectId === config.projectId &&
      app.options.appId === config.appId
    );
  }

  private async toIdentity(user: User): Promise<GoogleIdentity> {
    if (!user.email || !user.emailVerified) {
      throw new Error(
        'Google no proporcionó un correo electrónico verificado.',
      );
    }

    const emailPrefix = user.email.split('@')[0] || user.email;

    return {
      idToken: await user.getIdToken(),
      email: user.email,
      displayName: user.displayName?.trim() || emailPrefix,
      photoUrl: user.photoURL,
    };
  }
}
